import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import {
  initialTrainingImage,
  trainingImageSchema,
} from '~/utils/modules/supporting-object';

const CREATE_TRAINING_IMAGE = graphql(`
  mutation CreateTrainingImage($input: TrainingImageCreateInput!) {
    trainingImageCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

type Props = SOParent & {
  refetchQueries?: RefetchQueries;
};

export function TrainingImageCreateForm({
  outcropId,
  studyId,
  refetchQueries,
}: Props) {
  const [createTrainingImage, { loading, error }] = useMutation(
    CREATE_TRAINING_IMAGE,
    { refetchQueries },
  );

  const form = useZodForm({
    schema: trainingImageSchema,
    values: initialTrainingImage(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createTrainingImage({
        variables: {
          input: {
            ...values,
            outcropId,
            studyId,
          },
        },
      });
      toast.success('Training image created successfully.');
      form.reset();
    } catch (err) {
      console.log('Error creating training image', err);
      toast.error('There was a problem creating the training image.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="TRAINING_IMAGE" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
